import {
  TableCell,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  InputBase,
  alpha,
  Skeleton,
  Card,
  CardHeader,
  Divider,
  CardContent,
  Grid,
  Slide,
  Tabs,
  TextField,
  MenuItem,
  IconButton,
  Accordion,
  AccordionSummary,
  useMediaQuery
} from '@mui/material'
import { tableCellClasses } from '@mui/material/TableCell'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import { forwardRef, useState, useEffect } from 'react'
import { useTheme } from '@mui/material/styles'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import { ArrowBack, ArrowForward, FilterAltOff } from '@mui/icons-material'

export const SnackBarHideDuration = 4000

export function SnackBarTransition(props) {
  return <Slide {...props} direction="left" />
}

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14
  }
}))

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover
  }
  // hide last border
  // '&:last-child td, &:last-child th': {
  //   border: 0,
  // },
}))

export const ShowNoRecordsFound = () => (
  <Grid
    container
    spacing={0}
    direction="column"
    alignItems="center"
    justifyContent="center"
    sx={{ minHeight: '50vh', pt: 10 }}
  >
    <Grid item xs sm md>
      {/* <img alt="No Record Found" src="/static/img/infographics/404.png" /> */}
      <lottie-player
        src="/static/img/infographics/NoRecordsFound.json"
        background="transparent"
        speed="1"
        style={{ width: '400px', height: '400px' }}
        loop
        autoplay
      ></lottie-player>
    </Grid>
  </Grid>
)

export const ErrorScreen = ({ onClick, message }) => (
  <Grid
    container
    spacing={0}
    direction="column"
    alignItems="center"
    justifyContent="center"
    sx={{ minHeight: '50vh', pt: 10 }}
  >
    <Grid item xs sm md>
      <lottie-player
        src="/static/img/infographics/Error.json"
        background="transparent"
        speed="1"
        style={{ width: '400px', height: '400px' }}
        loop
        autoplay
      ></lottie-player>
    </Grid>
    <Grid item xs sm md>
      <Grid container sx={{ alignItems: 'center' }}>
        <Typography variant="h5">An Error has occured.</Typography>
        <Button variant="text" size="large" onClick={onClick}>
          Try Again.
        </Button>
      </Grid>
    </Grid>
  </Grid>
)

export const ImportScreen = () => (
  <Grid item sx={{ display: 'flex', justifyContent: 'center' }}>
    <lottie-player
      src="/static/img/infographics/import.json"
      background="transparent"
      speed="0.5"
      style={{ width: '400px', height: '400px' }}
      loop
      autoplay
    ></lottie-player>
  </Grid>
)

export const ExportScreen = () => (
  <Grid item sx={{ display: 'flex', justifyContent: 'center' }}>
    <lottie-player
      src="/static/img/infographics/export.json"
      background="transparent"
      speed="0.5"
      style={{ width: '400px', height: '400px' }}
      loop
      autoplay
    ></lottie-player>
  </Grid>
)

export const ActionButton = styled(Button)(({ theme }) => ({
  '& .MuiButton-root': {
    backgroundColor: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.common.blue
    }
  }
}))

export const ConfirmationDialog = ({
  visibility,
  okText,
  cancelText,
  onOkColor,
  message,
  note,
  title,
  onCancel,
  onOk
}) => (
  <Dialog
    open={visibility}
    maxWidth="xs"
    fullWidth
    // onClose={handleClose}
    aria-labelledby="alert-dialog-title"
    aria-describedby="alert-dialog-description"
  >
    <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
    <DialogContent>
      <Typography gutterBottom>{message}</Typography>
      {note && <Typography>{note}</Typography>}
    </DialogContent>

    <DialogActions>
      {cancelText && (
        <Button
          variant="outlined"
          size="medium"
          color="grey"
          onClick={onCancel}
        >
          {cancelText}
        </Button>
      )}
      {okText && (
        <Button
          variant="contained"
          size="medium"
          onClick={onOk}
          color={onOkColor ? onOkColor : 'error'}
        >
          {okText}
        </Button>
      )}
    </DialogActions>
  </Dialog>
)

export const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25)
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(3),
    width: 'auto'
  }
}))

export const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(2, 0),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}))

export const StyledInputBase = styled(InputBase)(({ theme }) => ({
  // color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(3, 3, 3, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%'
    // [theme.breakpoints.up('md')]: {
    //     width: '20ch',
    // },
  }
}))

export const SkeletonCard = () => (
  <Card>
    <CardContent>
      <Grid container direction="column">
        <Grid item>
          <Grid container justifyContent="space-between">
            <Grid item>
              <Skeleton variant="rectangular" width={44} height={44} />
            </Grid>
            <Grid item>
              <Skeleton variant="rectangular" width={34} height={34} />
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Skeleton variant="rectangular" sx={{ my: 2 }} height={40} />
        </Grid>
        <Grid item>
          <Skeleton variant="rectangular" height={30} />
        </Grid>
      </Grid>
    </CardContent>
  </Card>
)

const headerSX = {
  '& .MuiCardHeader-action': { mr: 0 }
}

const MainCard = forwardRef(
  (
    {
      border = true,
      boxShadow = false,
      children,
      content,
      contentSX = {},
      darkTitle,
      divider = true,
      elevation,
      secondary,
      shadow,
      sx = {},
      title,
      codeHighlight,
      ...others
    },
    ref
  ) => {
    const theme = useTheme()
    boxShadow = theme.palette.mode === 'dark' ? boxShadow || true : boxShadow

    return (
      <Card
        elevation={elevation || 0}
        ref={ref}
        {...others}
        sx={{
          ...sx,
          border: border ? '1px solid' : 'none',
          borderRadius: 2,
          borderColor:
            theme.palette.mode === 'dark'
              ? theme.palette.divider
              : theme.palette.divider, //"#6495ed",
          boxShadow:
            boxShadow && (!border || theme.palette.mode === 'dark')
              ? shadow || theme.customShadows.z1
              : 'inherit',
          // ':hover': {
          //     boxShadow: boxShadow ? shadow || theme.customShadows.z1 : 'inherit'
          // },
          '& pre': {
            m: 0,
            // p: '16px !important',
            fontFamily: theme.typography.fontFamily,
            fontSize: '0.75rem'
          }
        }}
      >
        {/* card header and action */}
        {!darkTitle && title && (
          <CardHeader
            sx={headerSX}
            titleTypographyProps={{ variant: 'subtitle1' }}
            title={title}
            action={secondary}
          />
        )}
        {darkTitle && title && (
          <CardHeader
            sx={headerSX}
            title={<Typography variant="h3">{title}</Typography>}
            action={secondary}
          />
        )}

        {/* content & header divider */}
        {title && divider && <Divider />}

        {/* card content */}
        {content && <CardContent sx={contentSX}>{children}</CardContent>}
        {!content && children}

        {/* card footer - clipboard & highlighter  */}
        {codeHighlight && (
          <>
            <Divider sx={{ borderStyle: 'dashed' }} />
            <Highlighter codeHighlight={codeHighlight} main>
              {children}
            </Highlighter>
          </>
        )}
      </Card>
    )
  }
)

export const StyledTabs = styled((props) => (
  <Tabs
    {...props}
    TabIndicatorProps={{
      children: <span className="MuiTabs-indicatorSpan" />
    }}
  />
))({
  '& .MuiTabs-indicator': {
    display: 'flex',
    justifyContent: 'center',
    maxWidth: 180,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 6
  },

  '& .MuiTabs-indicatorSpan': {
    maxWidth: 10
  }
})

MainCard.propTypes = {
  border: PropTypes.bool,
  boxShadow: PropTypes.bool,
  contentSX: PropTypes.object,
  darkTitle: PropTypes.bool,
  divider: PropTypes.bool,
  elevation: PropTypes.number,
  secondary: PropTypes.node,
  shadow: PropTypes.string,
  sx: PropTypes.object,
  title: PropTypes.string,
  codeHighlight: PropTypes.bool,
  content: PropTypes.bool,
  children: PropTypes.node
}

export function stringToColor(str) {
  let hash = 0
  let alpha = ((str.length * 25) / 100) % 1
  alpha = alpha < 0.5 ? 0.5 : alpha
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
    hash = hash & hash // Convert to 32bit integer
  }
  let color = (hash & 0x00ffffff).toString(16).toUpperCase()
  color = `#${'00000'.substring(0, 6 - color.length)}${color}`
  const green = str.length / 100
  const hexGreen = Math.floor(green * 255)
    .toString(16)
    .toUpperCase()
  const paddedGreen = hexGreen.padStart(2, '0')
  color = `${color.slice(0, 3)}${paddedGreen}${color.slice(5, 7)}`

  const hexAlpha = Math.floor(alpha * 255)
    .toString(16)
    .toUpperCase()
  const paddedAlpha = hexAlpha.padStart(2, '0')
  color = `${color}${paddedAlpha}`
  return color
}

export function stringAvatar(name) {
  let nameArray = name.trim().split(' ')
  return {
    sx: {
      bgcolor: stringToColor(name)
    },
    children:
      nameArray.length > 1
        ? `${nameArray[0][0]}${nameArray[1][0]}`
        : `${nameArray[0][0]}`
  }
}

export const LoadingDialog = ({ open }) => (
  <Dialog open={open} fullWidth maxWidth="md">
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '200px'
      }}
    >
      <Typography variant="h6" gutterBottom>
        Exporting data...
      </Typography>
      <CircularProgress />
    </Box>
  </Dialog>
)

export const CardCarousel = ({
  cards,
  autoSlideDuration,
  cardCount = 4,
  hideArrows,
  showIndicators = true,
  pauseOnHover
}) => {
  const theme = useTheme()
  const matches = {
    xs: useMediaQuery(theme.breakpoints.down('sm')),
    sm: useMediaQuery(theme.breakpoints.between('sm', 'md')),
    md: useMediaQuery(theme.breakpoints.between('md', 'lg')),
    lg: useMediaQuery(theme.breakpoints.up('lg'))
  }

  // Determine number of cards to show based on screen size
  // If cardCount is explicitly set to 1, ALWAYS use 1 regardless of screen size
  const responsiveCardCount = cardCount === 1 
    ? 1 
    : matches.xs
    ? 1
    : matches.sm
    ? 2
    : matches.md
    ? 3
    : cardCount
  
  // Force size to 100% when cardCount is 1 to ensure single card display
  const size = cardCount === 1 ? '100%' : `${100 / responsiveCardCount}%`

  const [currentIndex, setCurrentIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)

  // Recalculate max index when responsive card count changes
  // When cardCount is 1, maxIndex should be cards.length - 1 (one card per slide)
  const maxIndex = cardCount === 1 
    ? Math.max(0, cards?.length - 1)
    : Math.max(0, Math.ceil(cards?.length / responsiveCardCount) - 1)

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? maxIndex : prevIndex - 1))
  }

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex === maxIndex ? 0 : prevIndex + 1))
  }

  useEffect(() => {
    // Reset index when screen size changes or cardCount changes to prevent empty slides
    setCurrentIndex(0)
  }, [responsiveCardCount, cardCount])

  useEffect(() => {
    if (autoSlideDuration && !isPaused) {
      const interval = setInterval(() => {
        handleNext()
      }, autoSlideDuration)
      return () => clearInterval(interval)
    }
  }, [autoSlideDuration, currentIndex, isPaused, maxIndex])

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        paddingBottom: '10px'
      }}
      onMouseOver={() => {
        if (pauseOnHover) setIsPaused(true)
      }}
      onMouseOut={() => {
        if (pauseOnHover) setIsPaused(false)
      }}
    >
      <Box
        sx={{
          display: 'flex',
          transform: `translateX(-${currentIndex * 100}%)`,
          transition: 'transform 0.5s ease-in-out',
          width: cardCount === 1 ? `${cards?.length * 100}%` : '100%'
        }}
      >
        {cards?.map((card, index) => (
          <Box
            key={index}
            sx={{
              flex: cardCount === 1 ? '0 0 100%' : `0 0 ${size}`,
              padding: { xs: 1, sm: 1.5, md: 2 },
              width: cardCount === 1 ? '100%' : size,
              maxWidth: cardCount === 1 ? '100%' : size,
              minWidth: cardCount === 1 ? '100%' : 'auto'
            }}
          >
            {card}
          </Box>
        ))}
      </Box>
      {!hideArrows && cards?.length > (cardCount === 1 ? 1 : responsiveCardCount) && (
        <>
          <IconButton
            onClick={handlePrev}
            sx={{
              position: 'absolute',
              top: '50%',
              left: { xs: '0px', sm: '1px' },
              transform: 'translateY(-50%)',
              zIndex: 1,
              backgroundColor: 'rgba(255, 255, 255, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.8)'
              },
              width: { xs: 30, sm: 40 },
              height: { xs: 30, sm: 40 }
            }}
          >
            <ArrowBack fontSize={matches.xs ? 'small' : 'medium'} />
          </IconButton>
          <IconButton
            onClick={handleNext}
            sx={{
              position: 'absolute',
              top: '50%',
              right: { xs: '0px', sm: '1px' },
              transform: 'translateY(-50%)',
              zIndex: 1,
              backgroundColor: 'rgba(255, 255, 255, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.8)'
              },
              width: { xs: 30, sm: 40 },
              height: { xs: 30, sm: 40 }
            }}
          >
            <ArrowForward fontSize={matches.xs ? 'small' : 'medium'} />
          </IconButton>
        </>
      )}
      {showIndicators && cards?.length > responsiveCardCount && (
        <Box
          sx={{
            position: 'absolute',
            bottom: '5px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: { xs: '4px', sm: '6px', md: '8px' }
          }}
        >
          {Array.from({ length: maxIndex + 1 }).map((_, index) => (
            <Box
              key={index}
              onClick={() => setCurrentIndex(index)}
              sx={{
                width: { xs: '6px', sm: '8px', md: '10px' },
                height: { xs: '6px', sm: '8px', md: '10px' },
                borderRadius: '50%',
                backgroundColor:
                  index === currentIndex ? 'primary.main' : 'grey.400',
                transition: 'background-color 0.3s',
                cursor: 'pointer'
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

export default MainCard

export const SubBusinessFilter = ({
  subBusinessData,
  value,
  onChange,
  onClear
}) => (
  <Grid container mt={3}>
    <Grid item xs={12} sm={12} md={3}>
      <TextField
        label="Filter by Sub Business"
        variant="outlined"
        size="small"
        select
        fullWidth
        value={value}
        onChange={onChange}
        InputProps={{
          endAdornment: value ? (
            <IconButton onClick={onClear}>
              <FilterAltOff />
            </IconButton>
          ) : null
        }}
      >
        {subBusinessData?.map((option, i) => (
          <MenuItem key={i} value={option.sub_business_name}>
            {option.sub_business_name}
          </MenuItem>
        ))}
      </TextField>
    </Grid>
  </Grid>
)

export const EnhancedAccordion = ({
  index,
  handleExpandChange,
  expanded,
  children,
  sx
}) => (
  <Accordion
    style={{
      marginBottom: '10px',
      borderRadius: '8px',
      boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
      transition: 'transform 0.2s ease-in-out',
      background: 'linear-gradient(135deg, #f3f4f6, #ffffff)',
      border: '1px solid #e0e0e0'
    }}
    sx={sx}
    expanded={expanded === index} // Ensure this is correctly linked to the state
    onChange={handleExpandChange(index)} // Correctly pass the handler
  >
    {children}
  </Accordion>
)

export const EnhancedAccordionSummary = ({
  children,
  ExpandIcon,
  index,
  sx
}) => (
  <AccordionSummary
    expandIcon={<ExpandIcon />}
    aria-controls={`${index}a-content`}
    id={`${index}a-header`}
    sx={{
      '&:hover': {
        backgroundColor: '#e0f7fa',
        transform: 'scale(1.02)',
        boxShadow: '0px 6px 8px rgba(0, 0, 0, 0.15)'
      },
      borderRadius: '8px',
      padding: '10px 20px',
      ...sx
    }}
  >
    {children}
  </AccordionSummary>
)

export const ModernCard = styled(Card)(() => ({
  borderRadius: 16,
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
  boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 32px rgba(0,0,0,0.12)'
  }
}))

export const StatBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  borderRadius: 8,
  backgroundColor: 'rgba(245, 245, 245, 0.6)',
  margin: theme.spacing(0.5, 0),
  // hover effect
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: 'rgba(245, 245, 245, 0.8)'
  }
}))

export const GridRow = ({
  label,
  value,
  isHighlighted = false,
  valueColor = '#1976d2'
}) => {
  return (
    <Grid
      item
      md={8}
      m={1.5}
      p={1.5}
      sx={{
        backgroundColor: isHighlighted ? '#fff8e1' : '#ffffff',
        borderRadius: '10px',
        border: `1px solid ${isHighlighted ? '#ffe082' : '#e0e7ff'}`,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          transform: 'translateY(-1px)'
        }
      }}
    >
      <Grid
        container
        direction="row"
        justifyContent="space-around"
        alignItems="center"
      >
        <Grid item xs={5}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: '#1e293b',
                fontSize: '1rem',
                letterSpacing: '0.2px'
              }}
            >
              {label}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={5}>
          <Grid container justifyContent={'flex-end'}>
            <Typography
              variant="subtitle1"
              sx={{
                color: valueColor,
                fontWeight: 700,
                textAlign: 'right',
                fontSize: '1.1rem',
                padding: '2px 8px',
                borderRadius: '6px',
                backgroundColor: 'rgba(0,0,0,0.03)',
                display: 'inline-block'
              }}
            >
              {value}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}
